import { useState, useEffect, useCallback } from 'react';
import { useSeriesDetails } from './useSeriesDetails';
import userDataService from '../services/UserDataService';
import episodeSelectionService from '../services/EpisodeSelectionService';
import { useAuthContext } from '../contexts/AuthContext';

/**
 * useSeriesNavigation - Enhanced series navigation hook
 *
 * Combines useSeriesDetails with additional navigation logic for watch pages.
 * Provides season/episode selection, auto-play, and watch-specific features.
 */
const useSeriesNavigation = (seriesId, options = {}) => {
  const {
    autoPlay = false,
    autoAdvance = false,
    onEpisodeChange = null,
    onSeasonChange = null,
    onEpisodeSelect = null, // Add callback for explicit episode selection
    initialSeason = null,
    initialEpisode = null,
    initialEpisodeId = null, // Add support for episode ID
    watchMode = false,
    checkContinueWatching = false, // New option to check continue watching data
  } = options;

  const { isAuthenticated } = useAuthContext();

  // Track hook calls and parameter changes
  useEffect(() => {
    // Removed all debug logs from useSeriesNavigation.js
  });

  // Get series data from the base hook
  // Disable auto-selection of first episode when checking for continue watching
  const seriesData = useSeriesDetails(seriesId, {
    autoSelectFirstEpisode: !checkContinueWatching && !initialEpisodeId,
    initialEpisodeId,
  });
  // Removed all debug logs from useSeriesNavigation.js
  const {
    series,
    childChannels,
    currentChildChannel,
    episodes,
    selectedEpisode,
    relatedContent,
    loading,
    error,
    retry,
    selectChildChannel,
    selectEpisode,
  } = seriesData;

  // Additional state for navigation features
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [watchHistory, setWatchHistory] = useState(new Map());
  const [autoPlayNext, setAutoPlayNext] = useState(autoPlay);
  const [continueWatchingChecked, setContinueWatchingChecked] = useState(false);

  // Single initialization effect that handles all logic in the correct order
  useEffect(() => {
    if (!loading && !continueWatchingChecked && episodes.length > 0) {
      // Removed all debug logs from useSeriesNavigation.js

      const initializeEpisodeSelection = () => {
        try {
          // Step 1: Check if we should use centralized episode selection
          if (checkContinueWatching && isAuthenticated && series?._id) {
            // Removed all debug logs from useSeriesNavigation.js

            // Use the centralized episode selection service
            const selectionResult = episodeSelectionService.selectEpisodeForSeries(
              series._id,
              episodes,
              childChannels,
              currentChildChannel,
              initialEpisodeId,
            );

            // Removed all debug logs from useSeriesNavigation.js

            // Step 2: Execute the selection
            if (selectionResult.season && selectionResult.season._id !== currentChildChannel?._id) {
              // Removed all debug logs from useSeriesNavigation.js
              selectChildChannel(selectionResult.season);
            }

            if (selectionResult.episode && episodes.length > 0) {
              // Try to find the episode by ID first, then by title as fallback
              let episodeInCurrentSeason = episodes.find(
                episode => episode._id === selectionResult.episode._id,
              );

              if (!episodeInCurrentSeason) {
                // Fallback: try to match by title
                episodeInCurrentSeason = episodes.find(
                  episode => episode.title === selectionResult.episode.title,
                );
              }

              if (episodeInCurrentSeason) {
                // Removed all debug logs from useSeriesNavigation.js
                selectEpisode(episodeInCurrentSeason);
              } else {
                // Removed all debug logs from useSeriesNavigation.js
              }
            } else if (!selectionResult.episode && selectionResult.selectionReason === 'no-data') {
              // Removed all debug logs from useSeriesNavigation.js
            }
          } else if (initialEpisodeId) {
            // Use URL episode if specified
            // Removed all debug logs from useSeriesNavigation.js
            const targetEpisode = episodes.find(episode => episode._id === initialEpisodeId);
            if (targetEpisode) {
              selectEpisode(targetEpisode);
            } else {
              // Removed all debug logs from useSeriesNavigation.js
            }
          } else {
            // Default to first episode if no special logic applies
            // Removed all debug logs from useSeriesNavigation.js
            if (episodes.length > 0) {
              selectEpisode(episodes[0]);
            } else {
              // Removed all debug logs from useSeriesNavigation.js
            }
          }
        } catch (error) {
          // Removed all debug logs from useSeriesNavigation.js
        } finally {
          // Removed all debug logs from useSeriesNavigation.js
          setContinueWatchingChecked(true);
        }
      };

      initializeEpisodeSelection();
    } else {
      // Removed all debug logs from useSeriesNavigation.js
    }
  }, [
    loading,
    continueWatchingChecked,
    checkContinueWatching,
    isAuthenticated,
    series?._id,
    initialEpisodeId,
    episodes,
    childChannels,
    currentChildChannel,
    selectChildChannel,
    selectEpisode,
  ]);

  // Select episode after season changes using centralized service
  useEffect(() => {
    // Removed all debug logs from useSeriesNavigation.js

    if (
      continueWatchingChecked &&
      episodes.length > 0 &&
      !selectedEpisode &&
      checkContinueWatching &&
      isAuthenticated &&
      series?._id
    ) {
      // Removed all debug logs from useSeriesNavigation.js

      const selectEpisodeAfterSeasonChange = () => {
        try {
          // Removed all debug logs from useSeriesNavigation.js

          // Use the centralized episode selection service
          const selectionResult = episodeSelectionService.selectEpisodeForSeries(
            series._id,
            episodes,
            childChannels,
            currentChildChannel,
            initialEpisodeId, // Pass initialEpisodeId to maintain user selection priority
          );

          // Removed all debug logs from useSeriesNavigation.js

          if (selectionResult.episode && episodes.length > 0) {
            // Try to find the episode by ID first, then by title as fallback
            let episodeInCurrentSeason = episodes.find(
              episode => episode._id === selectionResult.episode._id,
            );

            if (!episodeInCurrentSeason) {
              // Fallback: try to match by title
              episodeInCurrentSeason = episodes.find(
                episode => episode.title === selectionResult.episode.title,
              );
            }

            if (episodeInCurrentSeason) {
              // Removed all debug logs from useSeriesNavigation.js
              selectEpisode(episodeInCurrentSeason);
            } else {
              // Removed all debug logs from useSeriesNavigation.js
            }
          } else {
            // Removed all debug logs from useSeriesNavigation.js
          }
        } catch (error) {
          // Removed all debug logs from useSeriesNavigation.js
        }
      };

      selectEpisodeAfterSeasonChange();
    } else {
      // Removed all debug logs from useSeriesNavigation.js
    }
  }, [
    continueWatchingChecked,
    episodes,
    selectedEpisode,
    checkContinueWatching,
    isAuthenticated,
    series?._id,
    childChannels,
    currentChildChannel,
    selectEpisode,
  ]);

  // Enhanced season selection with callback
  const handleSeasonSelect = useCallback(
    season => {
      selectChildChannel(season);
      setIsPlaying(false); // Stop playing when changing seasons

      if (onSeasonChange) {
        onSeasonChange(season);
      }
    },
    [selectChildChannel, onSeasonChange],
  );

  // Enhanced episode selection with auto-play and callback
  const handleEpisodeSelect = useCallback(
    episode => {
      // Call the reset callback before selecting the episode
      if (onEpisodeSelect) {
        onEpisodeSelect();
      }

      selectEpisode(episode);

      if (watchMode || autoPlay) {
        setIsPlaying(true);
      }

      // Track watch history
      setWatchHistory(prev =>
        new Map(prev).set(episode._id, {
          episodeId: episode._id,
          seasonId: currentChildChannel?._id,
          timestamp: Date.now(),
          position: 0,
        }),
      );

      if (onEpisodeChange) {
        onEpisodeChange(episode);
      }
    },
    [selectEpisode, watchMode, autoPlay, currentChildChannel, onEpisodeChange, onEpisodeSelect],
  );

  // Get next episode for auto-advance
  const getNextEpisode = useCallback(() => {
    if (!selectedEpisode || episodes.length === 0) {return null;}

    const currentIndex = episodes.findIndex(ep => ep._id === selectedEpisode._id);
    if (currentIndex === -1 || currentIndex === episodes.length - 1) {
      // Check if there's a next season
      if (childChannels.length > 0 && currentChildChannel) {
        const currentSeasonIndex = childChannels.findIndex(
          season => season._id === currentChildChannel._id,
        );
        if (currentSeasonIndex !== -1 && currentSeasonIndex < childChannels.length - 1) {
          return {
            nextSeason: childChannels[currentSeasonIndex + 1],
            nextEpisode: null, // Will be first episode of next season
          };
        }
      }
      return null;
    }

    return {
      nextSeason: null,
      nextEpisode: episodes[currentIndex + 1],
    };
  }, [selectedEpisode, episodes, childChannels, currentChildChannel]);

  // Get previous episode
  const getPreviousEpisode = useCallback(() => {
    if (!selectedEpisode || episodes.length === 0) {return null;}

    const currentIndex = episodes.findIndex(ep => ep._id === selectedEpisode._id);
    if (currentIndex <= 0) {
      // Check if there's a previous season
      if (childChannels.length > 0 && currentChildChannel) {
        const currentSeasonIndex = childChannels.findIndex(
          season => season._id === currentChildChannel._id,
        );
        if (currentSeasonIndex > 0) {
          return {
            previousSeason: childChannels[currentSeasonIndex - 1],
            previousEpisode: null, // Will be last episode of previous season
          };
        }
      }
      return null;
    }

    return {
      previousSeason: null,
      previousEpisode: episodes[currentIndex - 1],
    };
  }, [selectedEpisode, episodes, childChannels, currentChildChannel]);

  // Navigate to next episode
  const goToNextEpisode = useCallback(() => {
    const next = getNextEpisode();
    
    if (!next) {
      return false;
    }

    if (next.nextEpisode) {
      handleEpisodeSelect(next.nextEpisode);
      return true;
    } else if (next.nextSeason) {
      handleSeasonSelect(next.nextSeason);
      // Episode will be auto-selected when season changes
      return true;
    }

    return false;
  }, [getNextEpisode, handleEpisodeSelect, handleSeasonSelect, selectedEpisode, episodes]);

  // Navigate to previous episode
  const goToPreviousEpisode = useCallback(() => {
    const previous = getPreviousEpisode();
    if (!previous) {return false;}

    if (previous.previousEpisode) {
      handleEpisodeSelect(previous.previousEpisode);
      return true;
    } else if (previous.previousSeason) {
      handleSeasonSelect(previous.previousSeason);
      // Will need to select last episode of previous season
      return true;
    }

    return false;
  }, [getPreviousEpisode, handleEpisodeSelect, handleSeasonSelect]);

  // Update playback position
  const updatePlaybackPosition = useCallback(
    position => {
      setPlaybackPosition(position);

      if (selectedEpisode) {
        setWatchHistory(prev => {
          const newHistory = new Map(prev);
          const existing = newHistory.get(selectedEpisode._id) || {};
          newHistory.set(selectedEpisode._id, {
            ...existing,
            position,
            lastUpdated: Date.now(),
          });
          return newHistory;
        });
      }
    },
    [selectedEpisode],
  );

  // Mark episode as watched
  const markEpisodeWatched = useCallback(
    (episodeId = selectedEpisode?._id) => {
      if (!episodeId) {return;}

      setWatchHistory(prev => {
        const newHistory = new Map(prev);
        const existing = newHistory.get(episodeId) || {};
        newHistory.set(episodeId, {
          ...existing,
          watched: true,
          completedAt: Date.now(),
        });
        return newHistory;
      });
    },
    [selectedEpisode],
  );

  // Get watch progress for an episode
  const getEpisodeProgress = useCallback(
    episodeId => {
      return watchHistory.get(episodeId) || null;
    },
    [watchHistory],
  );

  // Check if episode is watched
  const isEpisodeWatched = useCallback(
    episodeId => {
      const progress = watchHistory.get(episodeId);
      return progress?.watched || false;
    },
    [watchHistory],
  );

  // Get continue watching episode (last watched but not completed)
  const getContinueWatchingEpisode = useCallback(() => {
    let lastWatched = null;
    let lastTimestamp = 0;

    for (const [episodeId, progress] of watchHistory) {
      if (!progress.watched && progress.position > 0 && progress.timestamp > lastTimestamp) {
        const episode = episodes.find(ep => ep._id === episodeId);
        if (episode) {
          lastWatched = { episode, progress };
          lastTimestamp = progress.timestamp;
        }
      }
    }

    return lastWatched;
  }, [watchHistory, episodes]);

  // Play/pause controls
  const play = useCallback(() => setIsPlaying(true), []);
  const pause = useCallback(() => setIsPlaying(false), []);
  const togglePlayback = useCallback(() => setIsPlaying(prev => !prev), []);

  // Auto-advance to next episode when current ends
  useEffect(() => {
    if (autoAdvance && autoPlayNext && !isPlaying) {
      const timer = setTimeout(() => {
        const success = goToNextEpisode();
        if (success) {
          setIsPlaying(true);
        }
      }, 1000); // 1 second delay before auto-advance

      return () => clearTimeout(timer);
    }
  }, [autoAdvance, autoPlayNext, isPlaying, goToNextEpisode]);

  return {
    // Base series data
    ...seriesData,

    // Enhanced navigation
    handleSeasonSelect,
    handleEpisodeSelect,
    goToNextEpisode,
    goToPreviousEpisode,
    getNextEpisode,
    getPreviousEpisode,

    // Playback controls
    isPlaying,
    play,
    pause,
    togglePlayback,
    playbackPosition,
    updatePlaybackPosition,

    // Watch history
    watchHistory,
    markEpisodeWatched,
    getEpisodeProgress,
    isEpisodeWatched,
    getContinueWatchingEpisode,

    // Auto-play settings
    autoPlayNext,
    setAutoPlayNext,

    // Utility functions
    hasNextEpisode: () => getNextEpisode() !== null,
    hasPreviousEpisode: () => getPreviousEpisode() !== null,

    // Watch mode specific
    watchMode,
  };
};

export default useSeriesNavigation;
